import CircularProgress from "@mui/material/CircularProgress";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";

function joinClasses(...tokens) {
  return tokens.filter(Boolean).join(" ");
}

export function GrowerSurface({
  as: Component = "section",
  children,
  className = "",
  padding = "p-6",
  tone = "white",
}) {
  const toneClass =
    tone === "subtle"
      ? "border border-slate-200 bg-[#fcfcfa] shadow-[0_10px_24px_rgba(15,23,42,0.04)]"
      : "border border-slate-200 bg-white shadow-[0_14px_34px_rgba(18,28,45,0.06)]";

  return (
    <Component className={joinClasses("rounded-[8px]", toneClass, padding, className)}>
      {children}
    </Component>
  );
}

export function GrowerSectionHeading({
  eyebrow = "",
  title,
  description = null,
  actions = null,
  className = "",
}) {
  return (
    <div className={joinClasses("flex flex-col gap-4 md:flex-row md:items-start md:justify-between", className)}>
      <div className="max-w-[860px]">
        {eyebrow ? (
          <div className="text-[12px] font-bold uppercase tracking-[0.16em] text-slate-400">
            {eyebrow}
          </div>
        ) : null}
        <h2 className="mt-1.5 text-[24px] font-black text-slate-900 md:text-[30px]">
          {title}
        </h2>
        {description ? (
          <p className="mt-2 text-[15px] leading-7 text-slate-600">{description}</p>
        ) : null}
      </div>
      {actions ? <div className="shrink-0">{actions}</div> : null}
    </div>
  );
}

export function GrowerToolbar({
  children,
  className = "",
}) {
  return (
    <div
      className={joinClasses(
        "border-y border-slate-200 bg-[#fcfcfa] px-3 py-3",
        className
      )}
    >
      {children}
    </div>
  );
}

export function GrowerTableSurface({
  title,
  meta = null,
  children,
  className = "",
}) {
  return (
    <GrowerSurface className={className} padding="p-0">
      {(title || meta) && (
        <div className="flex flex-col gap-2 border-b border-slate-200 px-5 py-4 md:flex-row md:items-center md:justify-between">
          <h3 className="text-[20px] font-black text-slate-900">
            {title}
          </h3>
          {meta ? <div className="text-sm text-slate-500">{meta}</div> : null}
        </div>
      )}
      <div className="px-4 py-4 md:px-5">{children}</div>
    </GrowerSurface>
  );
}

export function GrowerEmptyState({ title, description, className = "" }) {
  return (
    <div
      className={joinClasses(
        "rounded-[8px] border border-dashed border-slate-300 bg-white px-6 py-8 text-center",
        className
      )}
    >
      <h3 className="text-[22px] font-black text-slate-900">
        {title}
      </h3>
      <p className="mx-auto mt-2 max-w-[640px] text-[15px] leading-7 text-slate-600">
        {description}
      </p>
    </div>
  );
}

export function WorkspaceState({
  description = null,
  state = "empty",
  title,
  className = "",
}) {
  const isLoading = state === "loading";
  const isError = state === "error";

  return (
    <div
      aria-live="polite"
      className={joinClasses(
        "flex min-h-36 flex-col items-center justify-center px-5 py-8 text-center",
        isError ? "text-[#9b1c13]" : "text-slate-600",
        className
      )}
      role={isError ? "alert" : "status"}
    >
      {isLoading ? (
        <CircularProgress aria-hidden="true" size={24} sx={{ color: "#243245" }} />
      ) : isError ? (
        <ErrorOutlineIcon aria-hidden="true" sx={{ fontSize: 26 }} />
      ) : (
        <InboxOutlinedIcon aria-hidden="true" sx={{ fontSize: 26 }} />
      )}
      <p className="mt-3 text-[16px] font-bold text-current">{title}</p>
      {description ? (
        <p className="mt-1 max-w-[620px] text-sm leading-6 text-current opacity-80">
          {description}
        </p>
      ) : null}
    </div>
  );
}

export function GrowerToggleButton({
  active = false,
  children,
  className = "",
  onClick,
}) {
  return (
    <button
      className={joinClasses(
        "rounded-[4px] border px-3 py-2 text-sm font-semibold transition",
        active
          ? "border-[#243245] bg-[#243245] text-white"
          : "border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50",
        className
      )}
      onClick={onClick}
      type="button"
    >
      {children}
    </button>
  );
}
